import type {
  ArchiveFilters,
  ArchiveSearchResponse,
  ArchiveRecord,
  ArchiveImportOptions,
  ArchiveImportResult,
  ArchiveImportLogEntry,
  ArchiveProvider,
  ArchiveGezinslid,
  AkteRelatie,
  PaGezin,
  PaNietIngedeeldeAkte,
  PaOokIn,
  PaMeerdereMogelijk,
  PaStartpunt,
  PaStartpuntOvergeslagen,
  PaVoorouder,
  PaGestopt,
  PaMetingen,
  AfwijsReden,
  PersonAction,
  CitationData,
} from '../../types.ts';
import { fetchWithTimeout, parseErrorResponse, BULK_FETCH_TIMEOUT } from './base.ts';

export type ArchiveImportStatus = 'not_imported' | 'imported' | 'event_deleted';

export interface ArchiveImportStatusResponse {
  status: ArchiveImportStatus;
  importId?: string;
  importedAt?: string;
}

/** #1883 — stand van een akte voor een persoon, gedeeld in het project. */
export type AkteStandSoort = 'twijfel' | 'niet_van_hem';
export interface AkteStand {
  externalId: string;
  stand: AkteStandSoort;
  notitie: string | null;
  bijgewerkt: string;
  /** Weergavenaam van wie de keuze maakte; null zonder naam of als dat account weg is (nooit e-mail). */
  door: string | null;
  doorMij: boolean;
  /** #1884 — het blok waaruit deze stand komt; null = per akte gezet. */
  blokId?: string | null;
  /** #1957 — weg/null = de hele akte; een pid = één voorouder in die akte (afgewezen). */
  pid?: string | null;
  reden?: AfwijsReden | null;
}

const standPad = (projectId: string, personId: string) =>
  `/api/projects/${projectId}/people/${personId}/archive/stand`;

/**
 * #1957 — wie volgens de akte ouder, partner, getuige of iets anders is (PeckArchive, alleen
 * betaald). Gooit nooit: zonder antwoord zijn er gewoon geen relaties en blijft het aktescherm
 * zoals het was. Ook een losse export, zodat schermtests die ApiService nabootsen niet breken.
 */
export async function archiveGetRelaties(
  projectId: string,
  externalId: string
): Promise<{ beschikbaar: boolean; relaties: AkteRelatie[] | null }> {
  const geen = { beschikbaar: false, relaties: null };
  try {
    const query = new URLSearchParams({ id: externalId });
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/records/relaties?${query}`,
      undefined,
      15000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    return {
      beschikbaar: body?.beschikbaar === true,
      relaties: Array.isArray(body?.relaties) ? body.relaties : null,
    };
  } catch {
    return geen;
  }
}

export type GezinnenAntwoord = {
  beschikbaar: boolean;
  gezinnen: PaGezin[];
  nietIngedeeld: number;
  afgekapt: boolean;
  /** De akten achter `nietIngedeeld`; ontbreekt als PeckArchive geen lijst stuurt. */
  nietIngedeeldAkten?: PaNietIngedeeldeAkte[];
};

/** Gedeelde POST naar /archive/gezinnen (ouderparen- of akte-vorm). Gooit nooit. */
async function gezinnenOphalen(projectId: string, vraag: object): Promise<GezinnenAntwoord> {
  const geen = { beschikbaar: false, gezinnen: [], nietIngedeeld: 0, afgekapt: false };
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/gezinnen`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vraag),
      },
      30000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    return {
      beschikbaar: body?.beschikbaar === true,
      gezinnen: Array.isArray(body?.gezinnen) ? body.gezinnen : [],
      nietIngedeeld: typeof body?.nietIngedeeld === 'number' ? body.nietIngedeeld : 0,
      afgekapt: body?.afgekapt === true,
      ...(Array.isArray(body?.nietIngedeeldAkten)
        ? { nietIngedeeldAkten: body.nietIngedeeldAkten }
        : {}),
    };
  } catch {
    return geen;
  }
}

/**
 * #1957 (plan 2) — de gezinnen die bij ouderparen horen (PeckArchive, alleen betaald).
 * Gooit nooit: bij een fout is er gewoon geen gezinsblok.
 */
export async function archiveGezinnen(
  projectId: string,
  personId: string,
  paren: Array<{
    vader: { voornaam: string; achternaam: string };
    moeder: { voornaam: string; achternaam: string };
  }>,
  /** #1957 plan 5 — alleen akten met een geboortejaar in deze periode (tweede vraag na `afgekapt`). */
  periode?: { van: number; tot: number }
): Promise<GezinnenAntwoord> {
  return gezinnenOphalen(projectId, { personId, paren, ...(periode ? { periode } : {}) });
}

/**
 * #1957 (plan 3) — het gezin van de akte waarin de persoon kind is: zelfde antwoord als
 * `archiveGezinnen`, maar de vraag is een akte + pid in plaats van ouderparen. Gooit nooit.
 */
export async function archiveGezinnenAkte(
  projectId: string,
  personId: string,
  akte_id: string,
  pid: string
): Promise<GezinnenAntwoord> {
  return gezinnenOphalen(projectId, { personId, akte: { akte_id, pid } });
}

/**
 * #1957 (plan 3) — welke persoon in welke akte is deze boompersoon (PeckArchive, alleen betaald).
 * Gooit nooit: bij een fout zijn er geen startpunten.
 */
export async function archiveStartpunten(
  projectId: string,
  personId: string
): Promise<{
  beschikbaar: boolean;
  startpunten: PaStartpunt[];
  bronAkten: string[];
  overgeslagen: PaStartpuntOvergeslagen[];
}> {
  const geen = { beschikbaar: false, startpunten: [], bronAkten: [], overgeslagen: [] };
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/startpunten/${encodeURIComponent(personId)}`,
      undefined,
      60000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    return {
      beschikbaar: body?.beschikbaar === true,
      startpunten: Array.isArray(body?.startpunten) ? body.startpunten : [],
      bronAkten: Array.isArray(body?.bronAkten) ? body.bronAkten : [],
      overgeslagen: Array.isArray(body?.overgeslagen) ? body.overgeslagen : [],
    };
  } catch {
    return geen;
  }
}

/**
 * #1957 (plan 3) — de voorouders die PeckArchive voorstelt vanaf een startpunt (akte + pid); door de
 * genealoog afgewezen voorouders staan er gemarkeerd in. Gooit nooit.
 */
export async function archiveFamilie(
  projectId: string,
  personId: string,
  akte_id: string,
  pid: string,
  generaties?: number
): Promise<{
  beschikbaar: boolean;
  voorouders: PaVoorouder[];
  gestopt: PaGestopt[];
  ronde: number | null;
}> {
  const geen = { beschikbaar: false, voorouders: [], gestopt: [], ronde: null };
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/familie`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personId,
          akte_id,
          pid,
          ...(generaties !== undefined && { generaties }),
        }),
      },
      60000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    return {
      beschikbaar: body?.beschikbaar === true,
      voorouders: Array.isArray(body?.voorouders) ? body.voorouders : [],
      gestopt: Array.isArray(body?.gestopt) ? body.gestopt : [],
      ronde: typeof body?.ronde === 'number' ? body.ronde : null,
    };
  } catch {
    return geen;
  }
}

/** #1957 (plan 4b) — de drie niveaus die de preview toont; elk ander niveau valt weg (fail closed). */
export type PreviewNiveau = 'volgens-akte' | 'zeer waarschijnlijk' | 'waarschijnlijk';
const PREVIEW_NIVEAUS: readonly string[] = [
  'volgens-akte',
  'zeer waarschijnlijk',
  'waarschijnlijk',
];

/**
 * #1957 (plan 4b) — de preview voor gratis gebruikers: per eindpunt waar PeckArchive een voorouder weet,
 * alleen de plek (Sosa) en het niveau; nooit een naam, plaats, jaar of aantal akten. Het antwoord wordt
 * veld voor veld overgenomen: wat de server méér stuurt, komt hier niet doorheen. Een plek zonder bekend
 * niveau valt weg. Gooit nooit: bij een fout is er niets beschikbaar.
 */
export async function archivePreview(
  projectId: string,
  eindpunten: Array<{ personId: string; sosa: number }>
): Promise<{
  beschikbaar: boolean;
  plekken: Array<{ personId: string; sosa: number; niveau: PreviewNiveau }>;
}> {
  const geen = { beschikbaar: false, plekken: [] };
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/preview`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eindpunten: eindpunten.map((e) => ({ personId: e.personId, sosa: e.sosa })),
        }),
      },
      60000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    const plekken = (Array.isArray(body?.plekken) ? body.plekken : []).flatMap((p: unknown) => {
      const { personId, sosa, niveau } = (p ?? {}) as Record<string, unknown>;
      return typeof personId === 'string' &&
        typeof sosa === 'number' &&
        typeof niveau === 'string' &&
        PREVIEW_NIVEAUS.includes(niveau)
        ? [{ personId, sosa, niveau: niveau as PreviewNiveau }]
        : [];
    });
    return { beschikbaar: body?.beschikbaar === true, plekken };
  } catch {
    return geen;
  }
}

/**
 * #1957 (plan 2) — waar dezelfde persoon (pid) uit een akte nog meer voorkomt ("ook in").
 * `meerdereMogelijk` zijn kandidaten zonder zekerheid: alleen als tekst tonen, nooit als koppeling.
 * Gooit nooit.
 */
export async function archivePersoon(
  projectId: string,
  akteId: string,
  pid: string
): Promise<{ beschikbaar: boolean; ookIn: PaOokIn[]; meerdereMogelijk: PaMeerdereMogelijk[] }> {
  const geen = { beschikbaar: false, ookIn: [], meerdereMogelijk: [] };
  try {
    const query = new URLSearchParams({ akte_id: akteId, pid });
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/persoon?${query}`,
      undefined,
      15000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    return {
      beschikbaar: body?.beschikbaar === true,
      ookIn: Array.isArray(body?.ook_in) ? body.ook_in : [],
      meerdereMogelijk: Array.isArray(body?.meerdere_mogelijk) ? body.meerdere_mogelijk : [],
    };
  } catch {
    return geen;
  }
}

/** Sleutel van een persoon in een akte, voor de uitkomst van `archivePersoonBatch` (akte_id als GUID: klein, zonder accolades). */
export const persoonSleutel = (akteId: string, pid: string) =>
  `${akteId.replace(/[{}]/g, '').trim().toLowerCase()}|${pid}`;

/**
 * #1957 (plan 2) — "ook in" voor alle personen van een gezin in één verzoek. Een item dat PeckArchive
 * niet kon beantwoorden staat niet in `items`; de aanroeper laat die rij dan bij de geboorte.
 * Gooit nooit.
 */
export async function archivePersoonBatch(
  projectId: string,
  personId: string,
  items: Array<{ akte_id: string; pid: string }>
): Promise<{
  beschikbaar: boolean;
  items: Map<string, { ookIn: PaOokIn[]; meerdereMogelijk: PaMeerdereMogelijk[] }>;
}> {
  const geen = { beschikbaar: false, items: new Map() };
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/persoon`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personId, items }),
      },
      30000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    if (body?.beschikbaar !== true) return geen;
    const uit = new Map<string, { ookIn: PaOokIn[]; meerdereMogelijk: PaMeerdereMogelijk[] }>();
    for (const i of Array.isArray(body.items) ? body.items : []) {
      if (!i || i.fout || typeof i.akte_id !== 'string' || typeof i.pid !== 'string') continue;
      uit.set(persoonSleutel(i.akte_id, i.pid), {
        ookIn: Array.isArray(i.ook_in) ? i.ook_in : [],
        meerdereMogelijk: Array.isArray(i.meerdere_mogelijk) ? i.meerdere_mogelijk : [],
      });
    }
    return { beschikbaar: true, items: uit };
  } catch {
    return geen;
  }
}

/**
 * #1957 (plan 2) — staat het Gezinnen-paneel van PeckArchive aan (schakelaar en PeckArchive ingesteld)?
 * Ook voor gratis, die dan het slot zien. Gooit nooit: bij een fout is het uit.
 */
export async function archiveVondstenAan(projectId: string): Promise<boolean> {
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/vondsten`,
      undefined,
      10000
    );
    if (!res.ok) return false;
    return (await res.json())?.aan === true;
  } catch {
    return false;
  }
}

/**
 * #1957 (plan 4) — de meetuitkomsten van PeckArchive ("van 300 nagekeken klopten er 300"), voor de
 * uitleg bij een niveau. Gooit nooit: bij een fout is er geen uitleg.
 */
export async function archiveMetingen(
  projectId: string
): Promise<{ beschikbaar: boolean; metingen?: PaMetingen }> {
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/metingen`,
      undefined,
      10000
    );
    if (!res.ok) return { beschikbaar: false };
    const body = await res.json();
    return body?.beschikbaar === true && body.metingen
      ? { beschikbaar: true, metingen: body.metingen }
      : { beschikbaar: false };
  } catch {
    return { beschikbaar: false };
  }
}

/**
 * #1957 (plan 7) — de sleutels (`akte|pid`) van de archiefvoorstellen die de widget "Nieuw in de archieven"
 * in dit project al liet zien. Gooit nooit: bij een fout `beschikbaar: false`.
 */
export async function archiveGetoond(
  projectId: string
): Promise<{ beschikbaar: boolean; sleutels: string[] }> {
  const geen = { beschikbaar: false, sleutels: [] };
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/getoond`,
      undefined,
      10000
    );
    if (!res.ok) return geen;
    const body = await res.json();
    return body?.beschikbaar === true && Array.isArray(body.sleutels)
      ? { beschikbaar: true, sleutels: body.sleutels }
      : geen;
  } catch {
    return geen;
  }
}

/** Onthoudt dat deze voorstellen getoond zijn. True als het gelukt is; anders blijven ze de volgende keer nieuw. */
export async function archiveGetoondOpslaan(
  projectId: string,
  items: Array<{ akte_id: string; pid?: string }>
): Promise<boolean> {
  if (items.length === 0) return true;
  try {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/getoond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      },
      10000
    );
    if (!res.ok) return false;
    return (await res.json())?.beschikbaar === true;
  } catch {
    return false;
  }
}

export const archivesApi = {
  archiveGetRelaties,
  archiveGezinnen,
  archiveGezinnenAkte,
  archiveStartpunten,
  archiveFamilie,
  archivePreview,
  archiveVondstenAan,
  archiveMetingen,
  archiveGetoond,
  archiveGetoondOpslaan,
  archivePersoon,
  archivePersoonBatch,
  /** #1883 — standen van het project + wat dit account al bekeek, voor één persoon. */
  akteStandLezen: async (
    projectId: string,
    personId: string,
    opties?: { metPid?: boolean }
  ): Promise<{ standen: AkteStand[]; bekeken: Record<string, string> }> => {
    // #1957 — de pid-rijen (afgewezen voorouders) komen alleen mee als erom gevraagd wordt.
    const res = await fetchWithTimeout(
      `${standPad(projectId, personId)}${opties?.metPid ? '?metPid=1' : ''}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /** #1883 — zet twijfel / niet van hem, of draai terug ('herroepen'). Alleen eigenaar/bewerker. */
  akteStandZetten: async (
    projectId: string,
    personId: string,
    externalId: string,
    stand: AkteStandSoort | 'herroepen',
    notitie?: string,
    /** #1957 — afwijzen per voorouder: de pid in de akte en waarom. Alleen meegestuurd als gegeven. */
    extra?: { pid?: string; reden?: AfwijsReden }
  ): Promise<Omit<AkteStand, 'door'>> => {
    const res = await fetchWithTimeout(
      `${standPad(projectId, personId)}/${encodeURIComponent(externalId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stand,
          notitie,
          ...(extra?.pid !== undefined && { pid: extra.pid }),
          ...(extra?.reden !== undefined && { reden: extra.reden }),
        }),
      }
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  akteBekeken: async (projectId: string, personId: string, externalId: string): Promise<void> => {
    const res = await fetchWithTimeout(`${standPad(projectId, personId)}/bekeken`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ externalId }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  /**
   * #1884 — "niet van hem: hele blok" of terugdraaien. `zoekvraag` is dezelfde zoekvraag als de
   * laagweergave (met filterLaag + filterBlok): de server rekent het blok zelf uit.
   */
  akteStandBlok: async (
    projectId: string,
    personId: string,
    zoekvraag: Record<string, unknown>,
    stand: 'niet_van_hem' | 'herroepen',
    notitie?: string
  ): Promise<{ akten: number; gewijzigd: number }> => {
    const res = await fetchWithTimeout(`${standPad(projectId, personId)}/blok`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stand, zoekvraag, notitie }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /** #1883 — eenmalige overzetting van de localStorage-keuzes (#857/#916). */
  akteStandOverzetten: async (
    projectId: string,
    personId: string,
    data: { bekeken: Record<string, string>; nietRelevant: Record<string, string> }
  ): Promise<{ bekeken: number; nietVanHem: number; overgeslagen: number }> => {
    const res = await fetchWithTimeout(`${standPad(projectId, personId)}/overzetten`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Search external archives for genealogical records.
   */
  archiveSearch: async (
    projectId: string,
    params: {
      surname: string;
      givenNames?: string;
      birthYear?: number;
      place?: string;
      sourceType?: string;
      page?: number;
      pageSize?: number;
      provider?: ArchiveProvider;
      personId?: string;
      /**
       * Strict-mode: backend filtert OA's broad-match (4500 records) tot ~30-80
       * subject-treffers waar firstWord = given variant ÉN lastWord = surname.
       * Eerste call duurt 10-15s (paginated full fetch); volgende uit 24h cache.
       * Frontend doet UI-paginatie over de gefilterde set, geen backend-paginatie.
       */
      strictMode?: boolean;
      /** #1543 — vrije zoekvraag met OA-zoektekens; gaat ongewijzigd naar OA. */
      freeQuery?: string;
      /** NC #804 — sterfjaar: de levensperiode loopt t/m sterfjaar + 30. */
      deathYear?: number;
      /** NC #804 — partner(s) en ouders uit de boom voor de gezinsvragen. */
      familie?: ArchiveGezinslid[];
      /** NC #813 — patroniem uit de boom. */
      patronym?: string;
      /** NC #838 — bekende plaatsen uit de boom: per plaats een eigen zoekvraag. */
      plaatsen?: string[];
      /** #1881 — volledige datums uit de boom ("8 APR 1849", "ABT 1849") voor de lagen; niet in de zoeksleutel. */
      birthDate?: string;
      deathDate?: string;
      /** NC #804-naloop — lopende zoekactie: hoeveel tussentijdse treffers er al zijn. */
      sinds?: number;
      signal?: AbortSignal;
    } & ArchiveFilters
  ): Promise<ArchiveSearchResponse> => {
    const query = new URLSearchParams();
    query.set('surname', params.surname);
    if (params.givenNames) query.set('givenNames', params.givenNames);
    if (params.birthYear) query.set('birthYear', String(params.birthYear));
    if (params.place) query.set('place', params.place);
    if (params.sourceType) query.set('sourceType', params.sourceType);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.provider) query.set('provider', params.provider);
    if (params.personId) query.set('personId', params.personId);
    if (params.strictMode) query.set('strictMode', 'true');
    if (params.deathYear) query.set('deathYear', String(params.deathYear));
    if (params.familie && params.familie.length > 0)
      query.set('familie', JSON.stringify(params.familie));
    if (params.patronym) query.set('patronym', params.patronym);
    if (params.plaatsen && params.plaatsen.length > 0)
      query.set('plaatsen', params.plaatsen.join('|'));
    if (params.freeQuery) query.set('freeQuery', params.freeQuery);
    if (params.sinds) query.set('sinds', String(params.sinds));
    // #1714 — filters gelden op de server, over de volledige strikte lijst.
    if (params.filterRol && params.filterRol !== 'alle') query.set('filterRol', params.filterRol);
    if (params.filterType) query.set('filterType', params.filterType);
    if (params.filterArchief) query.set('filterArchief', params.filterArchief);
    if (params.filterPlaats) query.set('filterPlaats', params.filterPlaats);
    // #1881 — laagweergave: één laag in pagina's; de datums bepalen "past niet bij zijn leven".
    if (params.filterLaag) query.set('filterLaag', params.filterLaag);
    if (params.pagina) query.set('pagina', String(params.pagina));
    // #1884 — één naamgenoten-blok van de laag openen.
    if (params.filterBlok) query.set('filterBlok', params.filterBlok);
    if (params.birthDate) query.set('birthDate', params.birthDate);
    if (params.deathDate) query.set('deathDate', params.deathDate);

    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/search?${query}`,
      params.signal ? { signal: params.signal } : undefined,
      // Strict-mode kan tot 60 OA-pages ophalen (15s+) plus enrichment
      // (full-record fetch voor records met basisscore >= drempel, ~3 req/sec).
      // Bij 1500+ enrichment-records duurt dat ~8 min. Time-out op 10 min zodat
      // brede zoekopdrachten doorlopen tot completion. Tweede call cache-hit:
      // <100 ms. (#834)
      // Gewone zoekactie: één PeckArchive-vraag mag 30 s duren en wordt bij een te zware vraag
      // één keer herhaald (30 + 1 + 30 s), dus de browser wacht 65 s i.p.v. 30.
      params.strictMode ? 600000 : 65000
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * #1541 — verdiep (verrijk) de gegeven akten van een lopende strict-zoekopdracht.
   * `search` is dezelfde zoekvraag als bij archiveSearch, zodat de server de
   * juiste cache vindt; de voortgang komt daarna via de gewone polling binnen.
   */
  archiveEnrich: async (
    projectId: string,
    search: {
      surname: string;
      givenNames?: string;
      birthYear?: number;
      place?: string;
      sourceType?: string;
      page?: number;
      pageSize?: number;
      provider?: ArchiveProvider;
      freeQuery?: string;
      deathYear?: number;
      familie?: ArchiveGezinslid[];
      patronym?: string;
      plaatsen?: string[];
      /** #1881 — zelfde datums als de zoekvraag, zodat de laagweergave dezelfde cache vindt. */
      birthDate?: string;
      deathDate?: string;
      /** #1904 — de persoon; de laagweergave hangt af van zijn boomherkenning. */
      personId?: string;
    } & ArchiveFilters,
    externalIds: string[]
  ): Promise<{ queued: number; running?: boolean; enrichmentStats?: Record<string, unknown> }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/archive/enrich`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...search, externalIds }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Get proactive suggestions for a person (Business tier only).
   */
  archiveSuggest: async (
    projectId: string,
    params: {
      surname: string;
      givenNames?: string;
      birthYear?: number;
      provider?: ArchiveProvider;
    }
  ): Promise<ArchiveSearchResponse> => {
    const query = new URLSearchParams();
    query.set('surname', params.surname);
    if (params.givenNames) query.set('givenNames', params.givenNames);
    if (params.birthYear) query.set('birthYear', String(params.birthYear));
    if (params.provider) query.set('provider', params.provider);

    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/suggest?${query}`,
      undefined,
      15000
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Get a full archive record with all persons, events, and relationships.
   */
  archiveGetRecord: async (
    projectId: string,
    externalId: string,
    provider: ArchiveProvider = 'openarchieven'
  ): Promise<{ record: ArchiveRecord; cached: boolean }> => {
    const query = new URLSearchParams({
      id: externalId,
      provider,
    });

    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/records?${query}`,
      undefined,
      15000
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Import data from an archive record into the tree.
   */
  archiveImport: async (
    projectId: string,
    options: ArchiveImportOptions
  ): Promise<ArchiveImportResult> => {
    // #1769 — een import met ouders, scans en plaatsen duurt al snel langer dan
    // de standaard 5 s; de server maakt hem dan toch af. Ruim wachten dus.
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/import`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Multi-person record import (Fase 4 wizard). Hits POST /archive/import-multi
   * which accepts personActions[] + relationships[] + eventToCreate?, runs
   * everything in a single transaction with composite-key
   * `${recordKey}:p{idx}:e{eventIndex}` in archive_import_log.
   */
  archiveImportMulti: async (
    projectId: string,
    body: {
      recordKey: string;
      provider?: ArchiveProvider;
      externalUrl?: string;
      sourceTitle?: string;
      sourceType?: string;
      archiveCode?: string;
      citationFields?: Record<string, unknown>;
      citationData?: CitationData;
      personActions: PersonAction[];
      relationships?: Array<{
        fromPersonIndex: number;
        toPersonIndex: number;
        type: 'parent-child' | 'spouse';
        accepted: boolean;
      }>;
      eventToCreate?: { type: string; date?: string; place?: string };
      recordEventIndex?: number;
      /**
       * C3 — subjectRecordPersonIndex: index van het subject van de akte.
       * Server gebruikt dit om PersonAction.relationToSubject te resolveren
       * naar een rij in de relationships-tabel.
       */
      subjectRecordPersonIndex?: number;
      /**
       * C4 — beschikbare scans uit het record die als media moeten worden
       * geïmporteerd. Backend insertedt rij(en) in `media` + linkt via
       * `media_links` aan de subject-persoon.
       */
      scansToImport?: Array<{
        viewer?: string | null;
        thumbnail?: string | null;
        page?: number | null;
        caption?: string | null;
      }>;
      /** #1769 — één UUID per importpoging; een herhaald verzoek krijgt het eerste antwoord. */
      client_kenmerk?: string;
    }
  ): Promise<{
    success: boolean;
    sourceId: string;
    eventId: string | null;
    results: Array<{ recordPersonIndex: number; kind: string; personId?: string }>;
    herhaald?: boolean;
  }> => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/archive/import-multi`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Get import history for a person.
   */
  archiveGetImportLog: async (
    projectId: string,
    personId: string
  ): Promise<{
    imports: ArchiveImportLogEntry[];
    gebruiktBij?: Array<{ external_record_id: string; naam: string }>;
  }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/archive/import-log/${personId}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Lightweight status check used by the import UI to detect tombstones —
   * i.e. records that were imported earlier but whose event has since been
   * manually deleted from the tree. Returns:
   *   - 'not_imported'   → no archive_import_log row exists
   *   - 'imported'       → row exists and the linked event still lives
   *   - 'event_deleted'  → row exists but the linked event was soft-deleted
   *
   * Backend route: GET /api/projects/:projectId/archive/import-status/:personId/:externalRecordId
   */
  archiveGetImportStatus: async (
    projectId: string,
    personId: string,
    externalRecordId: string,
    provider: ArchiveProvider = 'openarchieven'
  ): Promise<ArchiveImportStatusResponse> => {
    const url =
      `/api/projects/${projectId}/archive/import-status/${personId}/` +
      `${encodeURIComponent(externalRecordId)}?provider=${encodeURIComponent(provider)}`;
    const res = await fetchWithTimeout(url);
    if (!res.ok) {
      if (res.status === 404) return { status: 'not_imported' };
      await parseErrorResponse(res);
    }
    return res.json();
  },

  /**
   * Project-wide import-log voor GEDCOM-export (#856, archieven-redesign Fase 6).
   * Retourneert raw archive_import_log rijen (snake_case kolommen) zodat de
   * gedcomCitation-helper ze rechtstreeks kan consumeren.
   */
  archiveGetImportLogProject: async (
    projectId: string
  ): Promise<{ imports: Array<Record<string, unknown>> }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/archive/import-log`);
    if (!res.ok) {
      // 403 (geen pro tier) of andere fout: graceful fallback — leeg retourneren zodat
      // export door kan zonder citation-data uitbreiding.
      if (res.status === 403 || res.status === 404) return { imports: [] };
      await parseErrorResponse(res);
    }
    return res.json();
  },
};
